import React, { useState } from 'react';
import { fetchAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FaTrophy, FaChartLine, FaArrowUp, FaArrowDown, FaUsers, FaMedal, FaChevronDown, FaChevronUp } from 'react-icons/fa';

export default function StudentMarks() {
    const { currentUser } = useAuth();
    const [analytics, setAnalytics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedExam, setSelectedExam] = useState(null);

    React.useEffect(() => {
        const loadAnalytics = async () => {
            if (!currentUser) return;
            try {
                const data = await fetchAPI(`/student/get_marks_analytics.php?user_id=${currentUser.id}`);
                setAnalytics(data);
            } catch (err) {
                console.error("Failed to load analytics", err);
            } finally {
                setLoading(false);
            }
        };
        loadAnalytics();
    }, [currentUser]);

    // Calculate statistics
    const stats = React.useMemo(() => {
        if (analytics.length === 0) return null;

        const studentMarks = analytics.map(a => a.student_marks);
        const average = studentMarks.reduce((a, b) => a + b, 0) / studentMarks.length;
        const highest = Math.max(...studentMarks);
        const lowest = Math.min(...studentMarks);

        // Calculate trend
        let trend = 'stable';
        if (analytics.length >= 4) {
            const recent = studentMarks.slice(-3).reduce((a, b) => a + b, 0) / 3;
            const older = studentMarks.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
            if (recent > older + 5) trend = 'improving';
            else if (recent < older - 5) trend = 'declining';
        }

        // Calculate rank statistics
        const avgClassAverage = analytics.reduce((sum, a) => sum + a.average_marks, 0) / analytics.length;
        const aboveAverage = studentMarks.filter((m, i) => m >= analytics[i].average_marks).length;
        const percentAboveAvg = (aboveAverage / analytics.length) * 100;

        return { average, highest, lowest, trend, avgClassAverage, percentAboveAvg };
    }, [analytics]);

    // Prepare chart data
    const chartData = React.useMemo(() => {
        return analytics.map((item, idx) => ({
            name: item.exam_name.length > 15 ? item.exam_name.substring(0, 15) + '...' : item.exam_name,
            fullName: item.exam_name,
            'Your Marks': item.student_marks,
            'Class Average': item.average_marks,
            'Highest Score': item.highest_marks,
            date: new Date(item.exam_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            examId: item.exam_id
        }));
    }, [analytics]);

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading analytics...</div>;

    const renderTopPerformers = (performers, total) => (
        <div className="mt-4 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-xl border-l-4 border-amber-400">
            <div className="flex items-center gap-2 mb-3">
                <FaMedal className="text-amber-600" size={18} />
                <span className="font-black text-sm text-gray-900 uppercase tracking-wider">Top Performers</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {performers.map((performer, idx) => (
                    <div
                        key={idx}
                        className={`p-3 rounded-xl border-2 ${idx === 0 ? 'bg-gradient-to-br from-yellow-100 to-amber-100 border-amber-300' :
                            idx === 1 ? 'bg-gradient-to-br from-gray-100 to-slate-100 border-gray-300' :
                                'bg-gradient-to-br from-orange-100 to-amber-100 border-orange-300'
                            }`}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`text-2xl ${idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'
                                }`}></span>
                            <div className="flex flex-col">
                                <span className="font-black text-gray-900 text-sm leading-tight">{performer.name}</span>
                                <span className={`text-[8px] font-black uppercase tracking-tighter w-fit px-1 rounded mt-0.5 ${performer.medium === 'English' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                    {performer.medium || 'Sinhala'}
                                </span>
                            </div>
                        </div>
                        <div className="text-xs text-gray-600 font-bold">{performer.index_number}</div>
                        <div className="text-lg font-black text-gray-900 mt-2">{performer.marks} marks</div>
                    </div>
                ))}
            </div>
            <div className="mt-3 text-xs text-gray-500 font-bold">
                Total Students: {total}
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto space-y-6 pb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 mx-4 md:mx-0">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        My Results & Analytics
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Track your academic performance and exam history.
                    </p>
                </div>
            </div>

            {analytics.length === 0 ? (
                <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-100 text-center mx-4 md:mx-0">
                    <div className="text-gray-400 text-lg font-bold">No results found yet</div>
                    <p className="text-gray-400 text-sm mt-2">Your exam results will appear here once they're uploaded by your instructor.</p>
                </div>
            ) : (
                <>
                    {/* Statistics Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 md:px-0">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 md:p-6 rounded-xl border border-blue-200/50">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] md:text-[10px] font-black text-blue-600 uppercase tracking-widest">Average</span>
                                <FaChartLine className="text-blue-600" size={14} />
                            </div>
                            <div className="text-2xl md:text-3xl font-black text-blue-900">{stats.average.toFixed(1)}%</div>
                            <div className="text-[9px] font-bold text-blue-600 mt-1">Class: {stats.avgClassAverage.toFixed(1)}%</div>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-4 md:p-6 rounded-xl border border-green-200/50">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] md:text-[10px] font-black text-green-600 uppercase tracking-widest">Highest</span>
                                <FaTrophy className="text-green-600" size={14} />
                            </div>
                            <div className="text-2xl md:text-3xl font-black text-green-900">{stats.highest}%</div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 md:p-6 rounded-xl border border-purple-200/50">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] md:text-[10px] font-black text-purple-600 uppercase tracking-widest">Above Avg</span>
                                <FaUsers className="text-purple-600" size={14} />
                            </div>
                            <div className="text-2xl md:text-3xl font-black text-purple-900">{stats.percentAboveAvg.toFixed(0)}%</div>
                            <div className="text-[9px] font-bold text-purple-600 mt-1">of exams</div>
                        </div>

                        <div className={`bg-gradient-to-br p-4 md:p-6 rounded-xl border ${stats.trend === 'improving' ? 'from-emerald-50 to-emerald-100/50 border-emerald-200/50' :
                            stats.trend === 'declining' ? 'from-red-50 to-red-100/50 border-red-200/50' :
                                'from-gray-50 to-gray-100/50 border-gray-200/50'
                            }`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${stats.trend === 'improving' ? 'text-emerald-600' :
                                    stats.trend === 'declining' ? 'text-red-600' : 'text-gray-600'
                                    }`}>Trend</span>
                                {stats.trend === 'improving' ? <FaArrowUp className="text-emerald-600" size={14} /> :
                                    stats.trend === 'declining' ? <FaArrowDown className="text-red-600" size={14} /> :
                                        <FaChartLine className="text-gray-600" size={14} />}
                            </div>
                            <div className={`text-lg md:text-xl font-black ${stats.trend === 'improving' ? 'text-emerald-900' :
                                stats.trend === 'declining' ? 'text-red-900' : 'text-gray-900'
                                }`}>
                                {stats.trend === 'improving' ? 'Improving' :
                                    stats.trend === 'declining' ? 'Declining' : 'Stable'}
                            </div>
                        </div>
                    </div>

                    {/* Chart - Hidden on very small screens if needed, or optimized */}
                    <div className="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-gray-100 mx-4 md:mx-0">
                        <h3 className="text-lg font-black text-gray-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                            <FaChartLine className="text-blue-600" /> Comparative Performance
                        </h3>
                        <div className="h-[250px] md:h-[350px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="date"
                                        stroke="#9ca3af"
                                        style={{ fontSize: '10px', fontWeight: 'bold' }}
                                        tick={{ dy: 10 }}
                                    />
                                    <YAxis
                                        stroke="#9ca3af"
                                        style={{ fontSize: '10px', fontWeight: 'bold' }}
                                        domain={[0, 100]}
                                        tickCount={6}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                            fontSize: '12px'
                                        }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }} />
                                    <Line type="monotone" dataKey="Your Marks" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="Class Average" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                    <Line type="monotone" dataKey="Highest Score" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Results: Table (Desktop) / Cards (Mobile) */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mx-4 md:mx-0">
                        <h3 className="text-lg font-black text-gray-900 uppercase tracking-widest mb-6">Detailed Results</h3>

                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Exam / Class</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Your Marks</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Class Avg</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Highest</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Grade</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-50 text-sm">
                                    {[...analytics].reverse().map((item) => (
                                        <React.Fragment key={item.exam_id}>
                                            <tr
                                                className="hover:bg-blue-50/30 transition-colors cursor-pointer"
                                                onClick={() => setSelectedExam(selectedExam === item.exam_id ? null : item.exam_id)}
                                            >
                                                <td className="px-6 py-4">
                                                    <span className="font-bold text-gray-900">{item.exam_name}</span>
                                                    <span className="block text-[10px] text-gray-400 uppercase font-bold tracking-wider">{item.class_name}</span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600">
                                                    {new Date(item.exam_date).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="font-black text-lg text-gray-900">{item.student_marks}</span>
                                                    <span className="text-gray-400 text-sm ml-1">/ 100</span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 font-bold">
                                                    {item.average_marks}
                                                </td>
                                                <td className="px-6 py-4 text-green-600 font-bold">
                                                    {item.highest_marks}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-lg font-black text-sm ${item.student_marks >= 75 ? 'bg-green-100 text-green-700' :
                                                        item.student_marks >= 65 ? 'bg-blue-100 text-blue-700' :
                                                            item.student_marks >= 50 ? 'bg-orange-100 text-orange-700' :
                                                                item.student_marks >= 45 ? 'bg-red-100 text-red-700' : 'bg-red-100 text-red-700'
                                                        }`}>
                                                        {item.student_marks >= 75 ? 'A' : item.student_marks >= 65 ? 'B' : item.student_marks >= 50 ? 'C' : item.student_marks >= 45 ? 'S' : 'F'}
                                                    </span>
                                                </td>
                                            </tr>
                                            {selectedExam === item.exam_id && (
                                                <tr>
                                                    <td colSpan="6" className="px-6 py-4">
                                                        {renderTopPerformers(item.top_performers, item.total_students)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards View */}
                        <div className="md:hidden space-y-4">
                            {[...analytics].reverse().map((item) => (
                                <div key={item.exam_id} className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-black text-gray-900 text-sm">{item.exam_name}</h4>
                                            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wide mt-1">{item.class_name}</p>
                                            <p className="text-[10px] text-gray-400 mt-0.5">{new Date(item.exam_date).toLocaleDateString()}</p>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-lg font-black text-xs ${item.student_marks >= 75 ? 'bg-green-100 text-green-700' :
                                            item.student_marks >= 50 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {item.student_marks >= 75 ? 'A' : item.student_marks >= 65 ? 'B' : item.student_marks >= 50 ? 'C' : item.student_marks >= 45 ? 'S' : 'F'}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2 py-3 border-t border-b border-gray-100 text-center">
                                        <div>
                                            <div className="text-[10px] text-gray-400 font-bold uppercase">Marks</div>
                                            <div className="text-xl font-black text-blue-600">{item.student_marks}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] text-gray-400 font-bold uppercase">Avg</div>
                                            <div className="text-xl font-bold text-gray-600">{item.average_marks}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] text-gray-400 font-bold uppercase">High</div>
                                            <div className="text-xl font-bold text-green-600">{item.highest_marks}</div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setSelectedExam(selectedExam === item.exam_id ? null : item.exam_id)}
                                        className="w-full mt-3 py-2 text-xs font-bold text-gray-500 uppercase flex items-center justify-center gap-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        {selectedExam === item.exam_id ? (
                                            <>Hide Top Performers <FaChevronUp /></>
                                        ) : (
                                            <>View Top Performers <FaChevronDown /></>
                                        )}
                                    </button>

                                    {selectedExam === item.exam_id && (
                                        <div className="mt-2 text-left">
                                            {renderTopPerformers(item.top_performers, item.total_students)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                    </div>
                </>
            )}
        </div>
    );
}
